import React, { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Grid,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    makeStyles,
} from '@material-ui/core';
import { getStaffCostos } from '../../services/staff-lector';

const useStyles = makeStyles(theme => ({
    filtersPanel: {
        marginBottom: theme.spacing(3),
        padding: theme.spacing(2.5),
        border: `1px solid ${theme.palette.border.main}`,
        borderRadius: 5,
        backgroundColor: theme.palette.common.white,
        boxShadow: theme.palette.shadow.search,
        display: 'flex',
        alignItems: 'flex-end',
        gap: theme.spacing(2),
        flexWrap: 'wrap',
    },
    filterGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5),
    },
    filterLabel: {
        fontSize: 12,
        fontWeight: 600,
        color: theme.palette.text.secondary,
    },
    filterField: {
        '& .MuiOutlinedInput-root': {
            borderRadius: 5,
            backgroundColor: theme.palette.surface.popover,
            fontSize: 13,
        },
        '& .MuiOutlinedInput-input': {
            paddingTop: theme.spacing(1),
            paddingBottom: theme.spacing(1),
        },
        width: 160,
    },
    selectField: {
        '& .MuiOutlinedInput-root': {
            borderRadius: 5,
            backgroundColor: theme.palette.surface.popover,
            fontSize: 13,
        },
        '& .MuiOutlinedInput-input': {
            paddingTop: theme.spacing(1),
            paddingBottom: theme.spacing(1),
        },
        minWidth: 148,
    },
    primaryButton: {
        borderRadius: 5,
        padding: theme.spacing(1, 2.25),
        textTransform: 'none',
        fontWeight: 700,
        color: theme.palette.common.white,
        background: `linear-gradient(180deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
        boxShadow: theme.palette.shadow.accent,
        '&:hover': {
            background: `linear-gradient(180deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        },
    },
    sectionLabel: {
        fontSize: 11,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.6px',
        color: theme.palette.text.section,
        marginBottom: theme.spacing(1.5),
        marginTop: theme.spacing(3),
        paddingBottom: theme.spacing(1),
        borderBottom: `1px solid ${theme.palette.border.section}`,
        '&:first-child': { marginTop: 0 },
    },
    summaryCard: {
        height: '100%',
        padding: theme.spacing(2),
        borderRadius: 5,
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.border.soft}`,
    },
    summaryLabel: {
        fontSize: 13,
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(1),
    },
    summaryValue: {
        fontSize: 26,
        fontWeight: 700,
        color: theme.palette.primary.main,
        lineHeight: 1.1,
    },
    summaryCaption: {
        fontSize: 12,
        color: theme.palette.text.disabled,
        marginTop: theme.spacing(0.5),
    },
    listContainer: {
        borderRadius: 5,
        border: `1px solid ${theme.palette.border.soft}`,
        boxShadow: theme.palette.shadow.card,
        overflow: 'hidden',
        backgroundColor: theme.palette.common.white,
        marginBottom: theme.spacing(3),
    },
    tableHead: {
        backgroundColor: theme.palette.surface.panel,
    },
    headCell: {
        color: theme.palette.text.section,
        fontWeight: 700,
        borderBottom: `2px solid ${theme.palette.border.main}`,
        whiteSpace: 'nowrap',
        fontSize: 12,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    bodyRow: {
        '&:nth-child(even)': {
            backgroundColor: theme.palette.surface.soft,
        },
    },
    bodyCell: {
        borderBottom: `1px solid ${theme.palette.border.subtle}`,
        color: theme.palette.text.secondary,
        whiteSpace: 'nowrap',
    },
    monoCell: {
        borderBottom: `1px solid ${theme.palette.border.subtle}`,
        color: theme.palette.text.secondary,
        fontFamily: 'monospace',
        fontSize: 12,
        whiteSpace: 'nowrap',
    },
    centeredState: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 180,
    },
}));

function MetricCard({ label, value, caption }) {
    const classes = useStyles();
    return (
        <Box className={classes.summaryCard}>
            <Typography className={classes.summaryLabel}>{label}</Typography>
            <Typography className={classes.summaryValue}>{value ?? '—'}</Typography>
            {caption && <Typography className={classes.summaryCaption}>{caption}</Typography>}
        </Box>
    );
}

function fmt(n, d = 0) {
    if (n == null) return '—';
    return Number(n).toLocaleString('es-AR', { minimumFractionDigits: d, maximumFractionDigits: d });
}

function todayStr() { return new Date().toISOString().slice(0, 10); }
function monthAgoStr() {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
}

export default function CostosTab() {
    const classes = useStyles();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [desde, setDesde] = useState(monthAgoStr());
    const [hasta, setHasta] = useState(todayStr());
    const [tipoServicio, setTipoServicio] = useState('');
    const [applied, setApplied] = useState({ desde: monthAgoStr(), hasta: todayStr(), tipo_servicio: '' });

    const fetchData = useCallback(() => {
        setLoading(true);
        getStaffCostos({ desde: applied.desde, hasta: applied.hasta, tipo_servicio: applied.tipo_servicio, top: 10 })
            .then(res => setData(res.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [applied]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleApply = e => {
        e.preventDefault();
        setApplied({ desde, hasta, tipo_servicio: tipoServicio });
    };

    const r = data?.resumen;
    const proc = data?.procesamiento;

    return (
        <Box>
            <form onSubmit={handleApply}>
                <Box className={classes.filtersPanel}>
                    <Box className={classes.filterGroup}>
                        <Typography className={classes.filterLabel}>Desde</Typography>
                        <TextField type="date" value={desde} onChange={e => setDesde(e.target.value)} variant="outlined" size="small" className={classes.filterField} InputLabelProps={{ shrink: true }} />
                    </Box>
                    <Box className={classes.filterGroup}>
                        <Typography className={classes.filterLabel}>Hasta</Typography>
                        <TextField type="date" value={hasta} onChange={e => setHasta(e.target.value)} variant="outlined" size="small" className={classes.filterField} InputLabelProps={{ shrink: true }} />
                    </Box>
                    <Box className={classes.filterGroup}>
                        <Typography className={classes.filterLabel}>Tipo de servicio</Typography>
                        <Select value={tipoServicio} onChange={e => setTipoServicio(e.target.value)} variant="outlined" displayEmpty className={classes.selectField}>
                            <MenuItem value="">Todos</MenuItem>
                            <MenuItem value="electricidad">Electricidad</MenuItem>
                            <MenuItem value="gas">Gas</MenuItem>
                            <MenuItem value="agua">Agua</MenuItem>
                        </Select>
                    </Box>
                    <Button type="submit" className={classes.primaryButton} size="small">
                        Aplicar
                    </Button>
                </Box>
            </form>

            {loading && <Box className={classes.centeredState}><CircularProgress size={32} /></Box>}

            {!loading && !data && (
                <Typography color="error">No se pudieron cargar los costos.</Typography>
            )}

            {!loading && data && (
                <>
                    <Typography className={classes.sectionLabel}>Resumen del período</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={6} sm={4} md={2}>
                            <MetricCard label="Scans totales" value={fmt(r?.n_scans)} />
                        </Grid>
                        <Grid item xs={6} sm={4} md={2}>
                            <MetricCard label="Scans con LLM" value={fmt(r?.n_scans_con_llm)} />
                        </Grid>
                        <Grid item xs={6} sm={4} md={2}>
                            <MetricCard label="Costo total" value={r?.total_cost_usd != null ? `$${fmt(r.total_cost_usd, 4)}` : '—'} caption="USD" />
                        </Grid>
                        <Grid item xs={6} sm={4} md={2}>
                            <MetricCard label="Costo prom./scan" value={r?.avg_cost_per_scan_usd != null ? `$${fmt(r.avg_cost_per_scan_usd, 4)}` : '—'} caption="USD" />
                        </Grid>
                        <Grid item xs={6} sm={4} md={2}>
                            <MetricCard label="Duración prom." value={r?.avg_duration_seconds != null ? `${fmt(r.avg_duration_seconds, 1)}s` : '—'} />
                        </Grid>
                        <Grid item xs={6} sm={4} md={2}>
                            <MetricCard
                                label="Tasa de éxito"
                                value={proc ? `${fmt(proc.tasa_exito_pct, 1)}%` : '—'}
                                caption={proc ? `${fmt(proc.ok)} OK / ${fmt(proc.error)} error` : ''}
                            />
                        </Grid>
                    </Grid>

                    <Typography className={classes.sectionLabel}>Por tipo de servicio</Typography>
                    <TableContainer className={classes.listContainer}>
                        <Table size="small">
                            <TableHead className={classes.tableHead}>
                                <TableRow>
                                    <TableCell className={classes.headCell}>Tipo</TableCell>
                                    <TableCell className={classes.headCell} align="right">Scans</TableCell>
                                    <TableCell className={classes.headCell} align="right">Con LLM</TableCell>
                                    <TableCell className={classes.headCell} align="right">Costo total (USD)</TableCell>
                                    <TableCell className={classes.headCell} align="right">Costo prom./scan</TableCell>
                                    <TableCell className={classes.headCell} align="right">Duración prom.</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(data.por_tipo || []).map(t => (
                                    <TableRow key={t.tipo_servicio} className={classes.bodyRow}>
                                        <TableCell className={classes.bodyCell} style={{ textTransform: 'capitalize', fontWeight: 600 }}>{t.tipo_servicio}</TableCell>
                                        <TableCell className={classes.bodyCell} align="right">{fmt(t.n_scans)}</TableCell>
                                        <TableCell className={classes.bodyCell} align="right">{fmt(t.n_scans_con_llm)}</TableCell>
                                        <TableCell className={classes.bodyCell} align="right">${fmt(t.total_cost_usd, 4)}</TableCell>
                                        <TableCell className={classes.bodyCell} align="right">{t.avg_cost_per_scan_usd != null ? `$${fmt(t.avg_cost_per_scan_usd, 4)}` : '—'}</TableCell>
                                        <TableCell className={classes.bodyCell} align="right">{t.avg_duration_seconds != null ? `${fmt(t.avg_duration_seconds, 1)}s` : '—'}</TableCell>
                                    </TableRow>
                                ))}
                                {!data.por_tipo?.length && <EmptyRow cols={6} />}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Typography className={classes.sectionLabel}>Por modelo</Typography>
                    <TableContainer className={classes.listContainer}>
                        <Table size="small">
                            <TableHead className={classes.tableHead}>
                                <TableRow>
                                    <TableCell className={classes.headCell}>Modelo</TableCell>
                                    <TableCell className={classes.headCell} align="right">Scans</TableCell>
                                    <TableCell className={classes.headCell} align="right">Costo (USD)</TableCell>
                                    <TableCell className={classes.headCell} align="right">Tokens input</TableCell>
                                    <TableCell className={classes.headCell} align="right">Tokens output</TableCell>
                                    <TableCell className={classes.headCell} align="right">Cache tokens</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(data.por_modelo || []).map(m => (
                                    <TableRow key={m.model} className={classes.bodyRow}>
                                        <TableCell className={classes.monoCell}>{m.model}</TableCell>
                                        <TableCell className={classes.bodyCell} align="right">{fmt(m.scans)}</TableCell>
                                        <TableCell className={classes.bodyCell} align="right">${fmt(m.cost_usd, 4)}</TableCell>
                                        <TableCell className={classes.bodyCell} align="right">{fmt(m.tokens_input)}</TableCell>
                                        <TableCell className={classes.bodyCell} align="right">{fmt(m.tokens_output)}</TableCell>
                                        <TableCell className={classes.bodyCell} align="right">{fmt(m.cache_read_tokens)}</TableCell>
                                    </TableRow>
                                ))}
                                {!data.por_modelo?.length && <EmptyRow cols={6} />}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Typography className={classes.sectionLabel}>Actividad diaria</Typography>
                    <TableContainer className={classes.listContainer}>
                        <Table size="small">
                            <TableHead className={classes.tableHead}>
                                <TableRow>
                                    <TableCell className={classes.headCell}>Fecha</TableCell>
                                    <TableCell className={classes.headCell} align="right">Scans</TableCell>
                                    <TableCell className={classes.headCell} align="right">Costo (USD)</TableCell>
                                    <TableCell className={classes.headCell} align="right">Tokens input</TableCell>
                                    <TableCell className={classes.headCell} align="right">Tokens output</TableCell>
                                    <TableCell className={classes.headCell} align="right">Duración (s)</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(data.por_dia || []).slice().reverse().map(d => (
                                    <TableRow key={d.fecha} className={classes.bodyRow}>
                                        <TableCell className={classes.bodyCell} style={{ fontWeight: 600 }}>{d.fecha}</TableCell>
                                        <TableCell className={classes.bodyCell} align="right">{fmt(d.scans)}</TableCell>
                                        <TableCell className={classes.bodyCell} align="right">${fmt(d.cost_usd, 4)}</TableCell>
                                        <TableCell className={classes.bodyCell} align="right">{fmt(d.tokens_input)}</TableCell>
                                        <TableCell className={classes.bodyCell} align="right">{fmt(d.tokens_output)}</TableCell>
                                        <TableCell className={classes.bodyCell} align="right">{fmt(d.duration_seconds, 1)}</TableCell>
                                    </TableRow>
                                ))}
                                {!data.por_dia?.length && <EmptyRow cols={6} />}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Typography className={classes.sectionLabel}>Top clientes por costo</Typography>
                    <TableContainer className={classes.listContainer}>
                        <Table size="small">
                            <TableHead className={classes.tableHead}>
                                <TableRow>
                                    <TableCell className={classes.headCell}>Cliente</TableCell>
                                    <TableCell className={classes.headCell} align="right">Scans</TableCell>
                                    <TableCell className={classes.headCell} align="right">Costo (USD)</TableCell>
                                    <TableCell className={classes.headCell} align="right">Duración total (s)</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(data.top_clientes || []).map(c => (
                                    <TableRow key={c.cliente_id} className={classes.bodyRow}>
                                        <TableCell className={classes.bodyCell}>
                                            <Typography variant="body2" style={{ fontWeight: 700, color: 'inherit' }}>{c.email}</Typography>
                                            {c.nombre && <Typography variant="caption" color="textSecondary">{c.nombre}</Typography>}
                                        </TableCell>
                                        <TableCell className={classes.bodyCell} align="right">{fmt(c.scans)}</TableCell>
                                        <TableCell className={classes.bodyCell} align="right">${fmt(c.cost_usd, 4)}</TableCell>
                                        <TableCell className={classes.bodyCell} align="right">{fmt(c.duration_seconds, 1)}</TableCell>
                                    </TableRow>
                                ))}
                                {!data.top_clientes?.length && <EmptyRow cols={4} />}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Typography className={classes.sectionLabel}>Top localizaciones por costo</Typography>
                    <TableContainer className={classes.listContainer}>
                        <Table size="small">
                            <TableHead className={classes.tableHead}>
                                <TableRow>
                                    <TableCell className={classes.headCell}>Localización</TableCell>
                                    <TableCell className={classes.headCell}>Tipos</TableCell>
                                    <TableCell className={classes.headCell} align="right">Scans</TableCell>
                                    <TableCell className={classes.headCell} align="right">Costo (USD)</TableCell>
                                    <TableCell className={classes.headCell} align="right">Duración total (s)</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(data.top_localizaciones || []).map(l => (
                                    <TableRow key={l.jerarquia_id} className={classes.bodyRow}>
                                        <TableCell className={classes.bodyCell}>
                                            <Typography variant="body2" style={{ fontWeight: 700, color: 'inherit' }}>{l.nombre}</Typography>
                                            <Typography variant="caption" color="textSecondary">ID {l.jerarquia_id}</Typography>
                                        </TableCell>
                                        <TableCell className={classes.bodyCell} style={{ textTransform: 'capitalize' }}>
                                            {(l.tipos_servicio || []).join(', ') || '—'}
                                        </TableCell>
                                        <TableCell className={classes.bodyCell} align="right">{fmt(l.scans)}</TableCell>
                                        <TableCell className={classes.bodyCell} align="right">${fmt(l.cost_usd, 4)}</TableCell>
                                        <TableCell className={classes.bodyCell} align="right">{fmt(l.duration_seconds, 1)}</TableCell>
                                    </TableRow>
                                ))}
                                {!data.top_localizaciones?.length && <EmptyRow cols={5} />}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}
        </Box>
    );
}

function EmptyRow({ cols }) {
    return (
        <TableRow>
            <TableCell colSpan={cols} align="center" style={{ padding: 24 }}>
                <Typography variant="body2" color="textSecondary">Sin datos</Typography>
            </TableCell>
        </TableRow>
    );
}
